import React, { useState, useEffect, useMemo } from "react";
import { Modal, ModalBody, Badge } from "react-bootstrap";
import { API_WEB_URLS } from "constants/constAPI";
import { toast } from "react-toastify";
import { Search, RefreshCw, AlertTriangle, ArrowRight, Package } from "react-feather";
import { API_HELPER } from "helpers/ApiHelper";

const QtyDifferenceModal = ({
  isOpen,
  onClose,
  fromDate,
  toDate,
  selectedLedgerIds = [],
  totalSellerQty = 0,
  totalBuyerQty = 0,
}) => {
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(15);
  const [activeTab, setActiveTab] = useState("modified");
  const [searchTerm, setSearchTerm] = useState("");
  const [auditData, setAuditData] = useState({
    summary: null,
    modifiedContracts: [],
    newContracts: [],
    dalaliChanges: [],
    selectionDiscrepancy: [],
  });

  const formatDateForAPI = (date) => {
    if (!date) return "";
    if (date instanceof Date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    }
    if (typeof date === "string") return date.split("T")[0];
    return date.toString();
  };

  const formatDateDisplay = (dateOrStr) => {
    if (!dateOrStr) return "-";
    if (typeof dateOrStr === "string" && dateOrStr.includes("/")) return dateOrStr;
    const d = new Date(dateOrStr);
    if (isNaN(d.getTime())) return String(dateOrStr);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getToDateMinusOneDisplay = (tDate) => {
    if (!tDate) return "-";
    const d = new Date(tDate);
    if (isNaN(d.getTime())) return String(tDate);
    d.setDate(d.getDate() - 1);
    return formatDateDisplay(d);
  };

  const loadAuditData = async (lookbackDays = days) => {
    setLoading(true);
    try {
      const selectedIdsArray = Array.from(selectedLedgerIds || []);
      const selectedIdsStr = selectedIdsArray.join(",");

      const formData = new FormData();
      formData.append("FromDate", formatDateForAPI(fromDate));
      formData.append("ToDate", formatDateForAPI(toDate));
      formData.append("SelectedLedgerIds", selectedIdsStr);
      formData.append("Days", lookbackDays.toString());

      const authToken = JSON.parse(localStorage.getItem("authUser"))?.token;
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      const primaryUrl = `${API_WEB_URLS.BASE}${API_WEB_URLS.QtyDifferenceAudit}/0/token`;

      let loadedSuccessfully = false;

      // 1. Try primary dedicated controller endpoint
      try {
        const response = await fetch(primaryUrl, {
          method: "POST",
          headers: headers,
          body: formData,
        });
        if (response.ok) {
          const resJson = await response.json();
          if (resJson && resJson.status === 200 && resJson.data) {
            setAuditData({
              summary: resJson.data.summary || null,
              modifiedContracts: resJson.data.modifiedContracts || [],
              newContracts: resJson.data.newContracts || [],
              dalaliChanges: resJson.data.dalaliChanges || [],
              selectionDiscrepancy: resJson.data.selectionDiscrepancy || [],
            });
            loadedSuccessfully = true;
          }
        }
      } catch (e) {
        // Will fallback to Master queries
      }

      // 2. Fallback to Master queries via GetList_Master (always works live)
      if (!loadedSuccessfully) {
        const masterBase = `${API_WEB_URLS.BASE}${API_WEB_URLS.MASTER}/0/token`;
        const fromDateStr = formatDateForAPI(fromDate);
        const toDateStr = formatDateForAPI(toDate);
        const dateRangeParam = (fromDateStr && toDateStr) ? `${fromDateStr}|${toDateStr}` : `Id/${lookbackDays}`;
        const pMod = API_HELPER.apiGET(`${masterBase}/QtyAuditModified/${encodeURIComponent(dateRangeParam)}/0`, headers).catch(() => null);
        const pNew = API_HELPER.apiGET(`${masterBase}/QtyAuditNew/${encodeURIComponent(dateRangeParam)}/0`, headers).catch(() => null);
        const pDalali = API_HELPER.apiGET(`${masterBase}/QtyAuditDalali/${encodeURIComponent(dateRangeParam)}/0`, headers).catch(() => null);
        const selParam = (fromDateStr && toDateStr) ? `${fromDateStr}|${toDateStr}|${selectedIdsStr}` : (selectedIdsStr || '0');
        const pSel = selectedIdsStr
          ? API_HELPER.apiGET(`${masterBase}/QtyAuditSelection/${encodeURIComponent(selParam)}/0`, headers).catch(() => null)
          : Promise.resolve(null);

        const [rMod, rNew, rDalali, rSel] = await Promise.all([pMod, pNew, pDalali, pSel]);

        const extractList = (res) => {
          if (!res) return [];
          if (Array.isArray(res)) return res;
          if (res.data && Array.isArray(res.data.dataList)) return res.data.dataList;
          if (res.data && Array.isArray(res.data)) return res.data;
          return [];
        };

        const modifiedContracts = extractList(rMod);
        const newContracts = extractList(rNew);
        const dalaliChanges = extractList(rDalali);
        const selectionDiscrepancy = extractList(rSel);

        setAuditData({
          summary: {
            TotalSellerQty: totalSellerQty,
            TotalBuyerQty: totalBuyerQty,
            QtyDifference: parseFloat((totalSellerQty - totalBuyerQty).toFixed(2)),
            ModifiedContractsCount: modifiedContracts.length,
            NewContractsCount: newContracts.length,
            DalaliChangesCount: dalaliChanges.length,
            SelectionDiscrepancyCount: selectionDiscrepancy.length,
          },
          modifiedContracts,
          newContracts,
          dalaliChanges,
          selectionDiscrepancy,
        });
      }
    } catch (err) {
      console.error("Error fetching Qty Difference Audit:", err);
      toast.error("Qty Difference Audit load karne me truti aayi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadAuditData(days);
    }
  }, [isOpen, days, fromDate, toDate, selectedLedgerIds]);

  // Calculations for Summary
  const diff = useMemo(() => {
    const s = auditData.summary ? parseFloat(auditData.summary.TotalSellerQty) || 0 : totalSellerQty;
    const b = auditData.summary ? parseFloat(auditData.summary.TotalBuyerQty) || 0 : totalBuyerQty;
    return parseFloat((s - b).toFixed(2));
  }, [auditData.summary, totalSellerQty, totalBuyerQty]);

  const hasSelection = (selectedLedgerIds?.length || selectedLedgerIds?.size || 0) > 0;

  // Filter lists based on search term
  const filterList = (list) => {
    if (!searchTerm.trim()) return list;
    const term = searchTerm.toLowerCase();
    return list.filter((item) => {
      return Object.values(item).some((val) =>
        val !== null && val !== undefined && String(val).toLowerCase().includes(term)
      );
    });
  };

  const isSameDayCreatedAndEdited = (item) => {
    if (!item) return false;

    const extractDate = (val) => {
      if (!val) return "";
      const str = String(val).trim();
      if (str.includes("T")) return str.split("T")[0];
      if (str.includes(" ")) return str.split(" ")[0];
      return str;
    };

    const normalizeDate = (val) => {
      const raw = extractDate(val);
      if (!raw) return "";
      if (raw.includes("/")) {
        const parts = raw.split("/");
        if (parts.length === 3) {
          return `${parts[2]}-${parts[1].padStart(2, "0")}-${parts[0].padStart(2, "0")}`;
        }
      }
      return raw;
    };

    const editedDate = normalizeDate(item.EditedOn);
    if (!editedDate) return false;

    // 1. If CreatedOn matches EditedOn date, ignore (same-day creation & edit)
    if (item.CreatedOn) {
      const createdDate = normalizeDate(item.CreatedOn);
      if (createdDate && createdDate === editedDate) {
        return true;
      }
    }

    // 2. If ContractDate matches EditedOn date, ignore
    if (item.ContractDate) {
      const contractDate = normalizeDate(item.ContractDate);
      if (contractDate && contractDate === editedDate) {
        return true;
      }
    }

    return false;
  };

  const isExcludedByDate = (item) => {
    if (!item) return false;

    // 1. Exclude same-day created & edited contracts
    if (isSameDayCreatedAndEdited(item)) return true;

    // 2. Exclude modifications on or after ToDate (e.g. today/current date)
    if (toDate && item.EditedOn) {
      const eDate = String(item.EditedOn).split("T")[0].split(" ")[0].trim();
      const tDate = formatDateForAPI(toDate);
      if (eDate && tDate && eDate >= tDate) {
        return true;
      }
    }

    return false;
  };

  const filteredModified = useMemo(() => {
    const validChanges = (auditData.modifiedContracts || []).filter((item) => {
      // Exclude contracts created/modified on same day OR modified on/after ToDate
      if (isExcludedByDate(item)) return false;

      const isQtyChanged = parseFloat(item.OldQty || 0) !== parseFloat(item.NewQty || 0);
      const isPartyChanged =
        (item.OldSellerName && item.NewSellerName && item.OldSellerName !== item.NewSellerName && item.OldSellerName !== '-' && item.NewSellerName !== '-') ||
        (item.OldBuyerName && item.NewBuyerName && item.OldBuyerName !== item.NewBuyerName && item.OldBuyerName !== '-' && item.NewBuyerName !== '-');
      const isRateChanged = parseFloat(item.OldRate || 0) !== parseFloat(item.NewRate || 0);
      const isRecognizedType = item.ChangeType && item.ChangeType !== 'Details Updated';
      return isQtyChanged || isPartyChanged || isRateChanged || isRecognizedType;
    });
    return filterList(validChanges);
  }, [auditData.modifiedContracts, searchTerm, toDate]);
  const filteredNew = useMemo(() => filterList(auditData.newContracts), [auditData.newContracts, searchTerm]);
  const filteredDalali = useMemo(() => filterList(auditData.dalaliChanges), [auditData.dalaliChanges, searchTerm]);
  const filteredSelection = useMemo(() => filterList(auditData.selectionDiscrepancy), [auditData.selectionDiscrepancy, searchTerm]);

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      size="xl"
      fullscreen="md-down"
      centered
      scrollable
      style={{ zIndex: 10001 }}
      contentClassName="shadow-lg border-0"
    >
      {/* Sleek, responsive Modal Header */}
      <div className="bg-dark text-white p-3 border-bottom border-secondary">
        {/* Top Row: Title + Status + Actions */}
        <div className="d-flex align-items-center justify-content-between gap-2">
          <div className="d-flex align-items-center gap-2 overflow-hidden" style={{ minWidth: 0 }}>
            <AlertTriangle
              size={20}
              className={`flex-shrink-0 ${Math.abs(diff) > 0.01 ? "text-warning" : "text-success"}`}
            />
            <div className="text-truncate">
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <span className="fw-bold fs-6 text-white">Qty Farq & Badlav Audit</span>
                <span
                  className={`badge ${Math.abs(diff) > 0.01 ? "bg-warning text-dark" : "bg-success"}`}
                  style={{ fontSize: "0.72rem" }}
                >
                  {Math.abs(diff) < 0.01 ? "Balanced (No Farq)" : `Farq: ${diff > 0 ? `+${diff.toFixed(2)}` : diff.toFixed(2)} MT`}
                </span>
              </div>
              <small className="text-muted d-block text-truncate" style={{ fontSize: "0.72rem" }}>
                Audit Range: {formatDateDisplay(fromDate)} se {getToDateMinusOneDisplay(toDate)} (ToDate - 1)
              </small>
            </div>
          </div>

          {/* Right Action buttons */}
          <div className="d-flex align-items-center gap-2 flex-shrink-0">
            <button
              type="button"
              className="btn btn-sm btn-outline-light d-flex align-items-center justify-content-center p-1"
              onClick={() => loadAuditData(days)}
              title="Refresh Audit Data"
              style={{ width: "30px", height: "30px", borderRadius: "6px" }}
            >
              <RefreshCw size={14} className={loading ? "spin" : ""} />
            </button>
            <button
              type="button"
              className="btn btn-sm btn-light text-dark fw-bold px-2 py-1"
              onClick={onClose}
              style={{ fontSize: "0.85rem", lineHeight: 1, borderRadius: "6px" }}
              title="Close Modal"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Second Row: Lookback pills & Scope badge */}
        <div className="d-flex align-items-center justify-content-between mt-2 pt-2 border-top border-secondary flex-wrap gap-2">
          <div className="d-flex align-items-center gap-1">
            <span className="text-muted small me-1" style={{ fontSize: "0.72rem" }}>Lookback:</span>
            <div className="btn-group btn-group-sm" role="group">
              {[7, 15, 30].map((d) => (
                <button
                  key={d}
                  type="button"
                  className={`btn btn-sm py-0 px-2 fw-semibold ${days === d ? "btn-primary" : "btn-outline-secondary text-light"}`}
                  onClick={() => setDays(d)}
                  style={{ fontSize: "0.72rem", height: "26px" }}
                >
                  {d} Din
                </button>
              ))}
            </div>
          </div>

          <span className="badge bg-secondary" style={{ fontSize: "0.7rem" }}>
            {hasSelection ? `${selectedLedgerIds?.length || selectedLedgerIds?.size} Parties Selected` : "Sabhi Parties (All Ledgers)"}
          </span>
        </div>
      </div>

      <ModalBody className="p-2 p-md-3" style={{ backgroundColor: "#f8f9fa", minHeight: "450px" }}>
        {/* Metric Cards - Ultra compact on mobile, clean cards on desktop */}
        <div className="row g-2 mb-2">
          <div className="col-6 col-md-3">
            <div className="card shadow-sm border-0 h-100 p-2" style={{ borderLeft: "4px solid #0d6efd" }}>
              <span className="text-muted" style={{ fontSize: "0.7rem", fontWeight: "600" }}>Total Seller Qty</span>
              <div className="d-flex align-items-baseline justify-content-between mt-1">
                <span className="fw-bold text-primary" style={{ fontSize: "1.1rem" }}>
                  {auditData.summary ? parseFloat(auditData.summary.TotalSellerQty).toFixed(2) : totalSellerQty.toFixed(2)}
                </span>
                <span className="badge bg-primary" style={{ fontSize: "0.65rem" }}>MT</span>
              </div>
            </div>
          </div>

          <div className="col-6 col-md-3">
            <div className="card shadow-sm border-0 h-100 p-2" style={{ borderLeft: "4px solid #dc3545" }}>
              <span className="text-muted" style={{ fontSize: "0.7rem", fontWeight: "600" }}>Total Buyer Qty</span>
              <div className="d-flex align-items-baseline justify-content-between mt-1">
                <span className="fw-bold text-danger" style={{ fontSize: "1.1rem" }}>
                  {auditData.summary ? parseFloat(auditData.summary.TotalBuyerQty).toFixed(2) : totalBuyerQty.toFixed(2)}
                </span>
                <span className="badge bg-danger" style={{ fontSize: "0.65rem" }}>MT</span>
              </div>
            </div>
          </div>

          <div className="col-6 col-md-3">
            <div
              className="card shadow-sm border-0 h-100 p-2"
              style={{
                borderLeft: Math.abs(diff) < 0.01 ? "4px solid #198754" : "4px solid #ffc107",
                backgroundColor: Math.abs(diff) < 0.01 ? "#f0fdf4" : "#fffbeb",
              }}
            >
              <span className="text-muted" style={{ fontSize: "0.7rem", fontWeight: "600" }}>Farq (Discrepancy)</span>
              <div className="d-flex align-items-baseline justify-content-between mt-1">
                <span className={`fw-bold ${Math.abs(diff) < 0.01 ? "text-success" : "text-dark"}`} style={{ fontSize: "1.1rem" }}>
                  {diff > 0 ? `+${diff.toFixed(2)}` : diff.toFixed(2)}
                </span>
                <span className={`badge ${Math.abs(diff) < 0.01 ? "bg-success" : "bg-warning text-dark"}`} style={{ fontSize: "0.65rem" }}>
                  {Math.abs(diff) < 0.01 ? "Balanced" : diff > 0 ? "Seller +" : "Buyer +"}
                </span>
              </div>
            </div>
          </div>

          <div className="col-6 col-md-3">
            <div className="card shadow-sm border-0 h-100 p-2" style={{ borderLeft: "4px solid #6f42c1" }}>
              <span className="text-muted" style={{ fontSize: "0.7rem", fontWeight: "600" }}>Audit Activity</span>
              <div className="d-flex align-items-baseline justify-content-between mt-1">
                <span className="fw-bold text-dark" style={{ fontSize: "0.9rem" }}>
                  {filteredModified.length + filteredNew.length + filteredDalali.length} Events
                </span>
                <span className="badge bg-purple text-white" style={{ backgroundColor: "#6f42c1", fontSize: "0.65rem" }}>
                  {days}D
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Scrollable Tabs Bar (Never wraps or breaks on mobile) */}
        <div
          className="d-flex align-items-center mb-2 p-1 bg-white rounded border shadow-sm"
          style={{
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            whiteSpace: "nowrap",
            gap: "6px",
          }}
        >
          <button
            type="button"
            className={`btn btn-sm text-nowrap py-1 px-3 fw-semibold rounded-pill ${
              activeTab === "modified" ? "btn-primary shadow-sm" : "btn-light text-dark"
            }`}
            style={{ fontSize: "0.78rem" }}
            onClick={() => setActiveTab("modified")}
          >
            🔄 Contract Badlav ({filteredModified.length})
          </button>

          <button
            type="button"
            className={`btn btn-sm text-nowrap py-1 px-3 fw-semibold rounded-pill ${
              activeTab === "new" ? "btn-primary shadow-sm" : "btn-light text-dark"
            }`}
            style={{ fontSize: "0.78rem" }}
            onClick={() => setActiveTab("new")}
          >
            ➕ Naye Contracts ({auditData.newContracts.length})
          </button>

          <button
            type="button"
            className={`btn btn-sm text-nowrap py-1 px-3 fw-semibold rounded-pill ${
              activeTab === "dalali" ? "btn-primary shadow-sm" : "btn-light text-dark"
            }`}
            style={{ fontSize: "0.78rem" }}
            onClick={() => setActiveTab("dalali")}
          >
            💰 Dalali Rate Badlav ({auditData.dalaliChanges.length})
          </button>

          {hasSelection && (
            <button
              type="button"
              className={`btn btn-sm text-nowrap py-1 px-3 fw-semibold rounded-pill ${
                activeTab === "selection" ? "btn-primary shadow-sm" : "btn-light text-dark"
              }`}
              style={{ fontSize: "0.78rem" }}
              onClick={() => setActiveTab("selection")}
            >
              ⚖️ Selection Farq ({auditData.selectionDiscrepancy.length})
            </button>
          )}
        </div>

        {/* Search Bar */}
        <div className="mb-2">
          <div className="input-group input-group-sm shadow-sm">
            <span className="input-group-text bg-white border-end-0">
              <Search size={14} className="text-muted" />
            </span>
            <input
              type="text"
              className="form-control border-start-0"
              placeholder="Search contract #, party name, item..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ fontSize: "0.8rem" }}
            />
            {searchTerm && (
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={() => setSearchTerm("")}
                style={{ fontSize: "0.75rem" }}
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Content Container */}
        <div className="card shadow-sm border-0">
          <div className="card-body p-0" style={{ maxHeight: "calc(100vh - 310px)", minHeight: "260px", overflowY: "auto" }}>
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 text-muted small">Pichhle {days} dino ka audit data load ho raha hai...</p>
              </div>
            ) : (
              <>
                {/* ============================================================== */}
                {/* TAB 1: MODIFIED CONTRACTS                                      */}
                {/* ============================================================== */}
                {activeTab === "modified" && (
                  <div>
                    {filteredModified.length === 0 ? (
                      <div className="text-center py-5 text-muted small">
                        Pichhle {days} dino me koi contract modification nahi mila.
                      </div>
                    ) : (
                      <>
                        {/* Mobile Cards View (<= 768px) */}
                        <div className="d-block d-md-none p-2">
                          {filteredModified.map((row, idx) => {
                            const isQtyChanged = row.OldQty !== row.NewQty;
                            const isPartyChanged =
                              row.OldSellerName !== row.NewSellerName || row.OldBuyerName !== row.NewBuyerName;
                            return (
                              <div
                                key={idx}
                                className="card mb-2 border shadow-sm"
                                style={{
                                  borderRadius: "8px",
                                  borderLeft: isQtyChanged ? "4px solid #ffc107 !important" : "4px solid #0d6efd !important",
                                  backgroundColor: isQtyChanged ? "#fffdf5" : "#ffffff",
                                }}
                              >
                                <div className="card-body p-2">
                                  {/* Header: Contract No, Date, Change Badge */}
                                  <div className="d-flex justify-content-between align-items-center mb-1">
                                    <div className="d-flex align-items-center gap-1">
                                      <span className="fw-bold text-dark fs-6">#{row.ContractNo}</span>
                                      <span className="text-muted" style={{ fontSize: "0.72rem" }}>
                                        • {row.ContractDate}
                                      </span>
                                    </div>
                                    <Badge
                                      bg={isQtyChanged ? "warning" : isPartyChanged ? "info" : "secondary"}
                                      className={isQtyChanged ? "text-dark" : "text-white"}
                                      style={{ fontSize: "0.68rem" }}
                                    >
                                      {row.ChangeType}
                                    </Badge>
                                  </div>

                                  {/* Item Name */}
                                  <div className="fw-semibold text-primary mb-1" style={{ fontSize: "0.82rem" }}>
                                    <Package size={12} className="me-1" />
                                    {row.ItemName}
                                  </div>

                                  {/* Qty Change Details Highlight */}
                                  <div
                                    className="p-1 mb-2 rounded border"
                                    style={{
                                      backgroundColor: isQtyChanged ? "#fff8e1" : "#f8f9fa",
                                      fontSize: "0.78rem",
                                    }}
                                  >
                                    <div className="d-flex justify-content-between align-items-center">
                                      <span className="text-muted">Quantity:</span>
                                      <div className="d-flex align-items-center gap-1">
                                        <span className="text-muted text-decoration-line-through">
                                          {parseFloat(row.OldQty || 0).toFixed(1)} MT
                                        </span>
                                        <ArrowRight size={11} className="text-muted" />
                                        <span className="fw-bold text-dark">
                                          {parseFloat(row.NewQty || 0).toFixed(1)} MT
                                        </span>
                                        {row.QtyDiff !== 0 && (
                                          <Badge
                                            bg={row.QtyDiff > 0 ? "success" : "danger"}
                                            style={{ fontSize: "0.65rem" }}
                                          >
                                            {row.QtyDiff > 0 ? `+${row.QtyDiff.toFixed(1)}` : row.QtyDiff.toFixed(1)} MT
                                          </Badge>
                                        )}
                                      </div>
                                    </div>

                                    {/* Rate Details */}
                                    <div className="d-flex justify-content-between align-items-center mt-1 pt-1 border-top border-light">
                                      <span className="text-muted">Rate:</span>
                                      <span className="fw-semibold text-dark">
                                        {row.OldRate !== row.NewRate ? (
                                          <>
                                            <span className="text-muted text-decoration-line-through me-1">
                                              ₹{row.OldRate}
                                            </span>
                                            ➔ ₹{row.NewRate}
                                          </>
                                        ) : (
                                          `₹${row.NewRate}`
                                        )}
                                      </span>
                                    </div>
                                  </div>

                                  {/* Parties (Seller & Buyer) */}
                                  <div className="small text-muted" style={{ fontSize: "0.74rem" }}>
                                    <div className="d-flex align-items-center gap-1 mb-1">
                                      <span className="fw-bold text-secondary" style={{ width: "42px" }}>Seller:</span>
                                      {row.OldSellerName !== row.NewSellerName ? (
                                        <span className="text-truncate">
                                          <span className="text-danger text-decoration-line-through me-1">
                                            {row.OldSellerName}
                                          </span>
                                          ➔ <span className="text-success fw-bold">{row.NewSellerName}</span>
                                        </span>
                                      ) : (
                                        <span className="text-dark fw-semibold text-truncate">{row.NewSellerName}</span>
                                      )}
                                    </div>

                                    <div className="d-flex align-items-center gap-1">
                                      <span className="fw-bold text-secondary" style={{ width: "42px" }}>Buyer:</span>
                                      {row.OldBuyerName !== row.NewBuyerName ? (
                                        <span className="text-truncate">
                                          <span className="text-danger text-decoration-line-through me-1">
                                            {row.OldBuyerName}
                                          </span>
                                          ➔ <span className="text-success fw-bold">{row.NewBuyerName}</span>
                                        </span>
                                      ) : (
                                        <span className="text-dark fw-semibold text-truncate">{row.NewBuyerName}</span>
                                      )}
                                    </div>
                                  </div>

                                  {/* Edited Timestamp Footer */}
                                  <div className="text-end text-muted mt-1 pt-1 border-top" style={{ fontSize: "0.68rem" }}>
                                    Changed on: {row.EditedOn}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Desktop Table View (> 768px) */}
                        <div className="d-none d-md-block table-responsive">
                          <table className="table table-hover table-sm mb-0 text-nowrap" style={{ fontSize: "0.78rem" }}>
                            <thead className="table-dark sticky-top">
                              <tr>
                                <th>Cont No</th>
                                <th>Date</th>
                                <th>Item</th>
                                <th>Seller (Purana → Naya)</th>
                                <th>Buyer (Purana → Naya)</th>
                                <th className="text-end">Old Qty</th>
                                <th className="text-end">New Qty</th>
                                <th className="text-center">Qty Farq</th>
                                <th className="text-end">Old Rate</th>
                                <th className="text-end">New Rate</th>
                                <th>Change Type</th>
                                <th>Edited On</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredModified.map((row, idx) => {
                                const isQtyChanged = row.OldQty !== row.NewQty;
                                return (
                                  <tr key={idx} style={{ backgroundColor: isQtyChanged ? "#fff8e1" : "inherit" }}>
                                    <td className="fw-bold">{row.ContractNo}</td>
                                    <td>{row.ContractDate}</td>
                                    <td>{row.ItemName}</td>
                                    <td>
                                      {row.OldSellerName !== row.NewSellerName ? (
                                        <span>
                                          <span className="text-danger text-decoration-line-through me-1">
                                            {row.OldSellerName}
                                          </span>
                                          <ArrowRight size={11} className="mx-1" />
                                          <span className="text-success fw-bold">{row.NewSellerName}</span>
                                        </span>
                                      ) : (
                                        row.NewSellerName
                                      )}
                                    </td>
                                    <td>
                                      {row.OldBuyerName !== row.NewBuyerName ? (
                                        <span>
                                          <span className="text-danger text-decoration-line-through me-1">
                                            {row.OldBuyerName}
                                          </span>
                                          <ArrowRight size={11} className="mx-1" />
                                          <span className="text-success fw-bold">{row.NewBuyerName}</span>
                                        </span>
                                      ) : (
                                        row.NewBuyerName
                                      )}
                                    </td>
                                    <td className="text-end text-muted">{parseFloat(row.OldQty || 0).toFixed(2)}</td>
                                    <td className="text-end fw-bold">{parseFloat(row.NewQty || 0).toFixed(2)}</td>
                                    <td className="text-center">
                                      {row.QtyDiff !== 0 ? (
                                        <Badge bg={row.QtyDiff > 0 ? "success" : "danger"}>
                                          {row.QtyDiff > 0 ? `+${parseFloat(row.QtyDiff).toFixed(2)}` : parseFloat(row.QtyDiff).toFixed(2)}
                                        </Badge>
                                      ) : (
                                        <span className="text-muted">-</span>
                                      )}
                                    </td>
                                    <td className="text-end text-muted">₹{parseFloat(row.OldRate || 0).toFixed(2)}</td>
                                    <td className="text-end fw-bold">₹{parseFloat(row.NewRate || 0).toFixed(2)}</td>
                                    <td>
                                      <Badge bg={isQtyChanged ? "warning" : "info"} className={isQtyChanged ? "text-dark" : "text-white"}>
                                        {row.ChangeType}
                                      </Badge>
                                    </td>
                                    <td className="text-muted" style={{ fontSize: "0.72rem" }}>
                                      {row.EditedOn}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* ============================================================== */}
                {/* TAB 2: NEW CONTRACTS                                           */}
                {/* ============================================================== */}
                {activeTab === "new" && (
                  <div>
                    {filteredNew.length === 0 ? (
                      <div className="text-center py-5 text-muted small">
                        Pichhle {days} dino me koi naya contract create nahi hua.
                      </div>
                    ) : (
                      <>
                        {/* Mobile Cards View */}
                        <div className="d-block d-md-none p-2">
                          {filteredNew.map((row, idx) => (
                            <div key={idx} className="card mb-2 border shadow-sm" style={{ borderRadius: "8px" }}>
                              <div className="card-body p-2">
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                  <span className="fw-bold text-dark fs-6">#{row.ContractNo}</span>
                                  <span className="text-muted" style={{ fontSize: "0.72rem" }}>
                                    {row.ContractDate}
                                  </span>
                                </div>
                                <div className="fw-semibold text-primary mb-1" style={{ fontSize: "0.82rem" }}>
                                  {row.ItemName}
                                </div>
                                <div className="d-flex justify-content-between align-items-center bg-light p-1 rounded mb-1" style={{ fontSize: "0.78rem" }}>
                                  <span>
                                    Qty: <strong className="text-dark">{parseFloat(row.Qty || 0).toFixed(1)} MT</strong>
                                  </span>
                                  <span>
                                    Rate: <strong className="text-dark">₹{parseFloat(row.Rate || 0).toFixed(2)}</strong>
                                  </span>
                                  <span className="text-success fw-bold">
                                    ₹{parseFloat(row.TotalAmount || row.Qty * row.Rate || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                                  </span>
                                </div>
                                <div className="small text-muted" style={{ fontSize: "0.74rem" }}>
                                  <div className="text-truncate">Seller: <strong className="text-dark">{row.SellerName}</strong></div>
                                  <div className="text-truncate">Buyer: <strong className="text-dark">{row.BuyerName}</strong></div>
                                </div>
                                <div className="text-end text-muted mt-1 pt-1 border-top" style={{ fontSize: "0.68rem" }}>
                                  Created: {row.CreatedOn}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Desktop Table View */}
                        <div className="d-none d-md-block table-responsive">
                          <table className="table table-hover table-sm mb-0 text-nowrap" style={{ fontSize: "0.78rem" }}>
                            <thead className="table-dark sticky-top">
                              <tr>
                                <th>Cont No</th>
                                <th>Date</th>
                                <th>Seller Party</th>
                                <th>Buyer Party</th>
                                <th>Item</th>
                                <th className="text-end">Quantity (MT)</th>
                                <th className="text-end">Rate</th>
                                <th className="text-end">Total Amount</th>
                                <th>Created On</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredNew.map((row, idx) => (
                                <tr key={idx}>
                                  <td className="fw-bold">{row.ContractNo}</td>
                                  <td>{row.ContractDate}</td>
                                  <td className="text-truncate" style={{ maxWidth: "200px" }}>{row.SellerName}</td>
                                  <td className="text-truncate" style={{ maxWidth: "200px" }}>{row.BuyerName}</td>
                                  <td>{row.ItemName}</td>
                                  <td className="text-end fw-bold">{parseFloat(row.Qty || 0).toFixed(2)}</td>
                                  <td className="text-end">₹{parseFloat(row.Rate || 0).toFixed(2)}</td>
                                  <td className="text-end fw-bold text-success">
                                    ₹{parseFloat(row.TotalAmount || row.Qty * row.Rate || 0).toFixed(2)}
                                  </td>
                                  <td className="text-muted" style={{ fontSize: "0.72rem" }}>{row.CreatedOn}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* ============================================================== */}
                {/* TAB 3: DALALI RATE CHANGES                                     */}
                {/* ============================================================== */}
                {activeTab === "dalali" && (
                  <div>
                    {filteredDalali.length === 0 ? (
                      <div className="text-center py-5 text-muted small">
                        Pichhle {days} dino me koi Dalali Rate change nahi hua.
                      </div>
                    ) : (
                      <>
                        {/* Mobile Cards View */}
                        <div className="d-block d-md-none p-2">
                          {filteredDalali.map((row, idx) => (
                            <div key={idx} className="card mb-2 border shadow-sm" style={{ borderRadius: "8px" }}>
                              <div className="card-body p-2">
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                  <span className="fw-bold text-dark text-truncate" style={{ fontSize: "0.85rem", maxWidth: "70%" }}>
                                    {row.LedgerName}
                                  </span>
                                  <Badge bg={row.ActionType === "Updated" ? "warning" : "success"} className={row.ActionType === "Updated" ? "text-dark" : "text-white"} style={{ fontSize: "0.68rem" }}>
                                    {row.ActionType}
                                  </Badge>
                                </div>
                                <div className="d-flex justify-content-between align-items-center mb-1 text-muted" style={{ fontSize: "0.74rem" }}>
                                  <span>Item: <strong className="text-dark">{row.ItemName}</strong></span>
                                  <span>Type: <strong className="text-primary">{row.TypeDisplay}</strong></span>
                                </div>
                                <div className="d-flex justify-content-between align-items-center bg-light p-1 rounded" style={{ fontSize: "0.78rem" }}>
                                  <span>Rate: <strong className="text-dark fs-6">₹{row.DalaliRate}</strong> / MT</span>
                                  <span>Effective: <strong className="text-dark">{row.EffectiveDateFormatted || "-"}</strong></span>
                                </div>
                                <div className="text-end text-muted mt-1 pt-1 border-top" style={{ fontSize: "0.68rem" }}>
                                  Changed on: {row.ChangedOn}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Desktop Table View */}
                        <div className="d-none d-md-block table-responsive">
                          <table className="table table-hover table-sm mb-0 text-nowrap" style={{ fontSize: "0.78rem" }}>
                            <thead className="table-dark sticky-top">
                              <tr>
                                <th>Party Name</th>
                                <th>Item</th>
                                <th>Party Type</th>
                                <th className="text-end">Dalali Rate</th>
                                <th>Effective Date</th>
                                <th>Action Type</th>
                                <th>Changed On</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredDalali.map((row, idx) => (
                                <tr key={idx}>
                                  <td className="fw-bold text-truncate" style={{ maxWidth: "220px" }}>{row.LedgerName}</td>
                                  <td>{row.ItemName}</td>
                                  <td>
                                    <Badge bg={row.TypeDisplay?.includes("Buyer") ? "danger" : "primary"}>
                                      {row.TypeDisplay}
                                    </Badge>
                                  </td>
                                  <td className="text-end fw-bold">₹{parseFloat(row.DalaliRate || 0).toFixed(2)}</td>
                                  <td>{row.EffectiveDateFormatted || "-"}</td>
                                  <td>
                                    <Badge bg={row.ActionType === "Updated" ? "warning" : "success"} className={row.ActionType === "Updated" ? "text-dark" : "text-white"}>
                                      {row.ActionType}
                                    </Badge>
                                  </td>
                                  <td className="text-muted" style={{ fontSize: "0.72rem" }}>{row.ChangedOn}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* ============================================================== */}
                {/* TAB 4: SELECTION BREAKDOWN                                     */}
                {/* ============================================================== */}
                {activeTab === "selection" && hasSelection && (
                  <div>
                    {filteredSelection.length === 0 ? (
                      <div className="text-center py-5 text-muted small">
                        Selected parties ke sabhi counter-party contracts balanced hain.
                      </div>
                    ) : (
                      <>
                        {/* Mobile Cards View */}
                        <div className="d-block d-md-none p-2">
                          {filteredSelection.map((row, idx) => (
                            <div key={idx} className="card mb-2 border shadow-sm" style={{ borderRadius: "8px", borderLeft: "4px solid #fd7e14" }}>
                              <div className="card-body p-2">
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                  <span className="fw-bold text-dark fs-6">#{row.ContractNo}</span>
                                  <span className="badge bg-warning text-dark" style={{ fontSize: "0.7rem" }}>
                                    {parseFloat(row.Qty || 0).toFixed(1)} MT
                                  </span>
                                </div>
                                <div className="fw-semibold text-primary mb-1" style={{ fontSize: "0.8rem" }}>
                                  {row.ItemName} • {row.ContractDate}
                                </div>
                                <div className="small p-1 rounded bg-light mb-1" style={{ fontSize: "0.74rem" }}>
                                  <div>Seller: <strong className={row.IsSellerSelected ? "text-success" : "text-muted"}>{row.SellerName}</strong></div>
                                  <div>Buyer: <strong className={row.IsBuyerSelected ? "text-success" : "text-muted"}>{row.BuyerName}</strong></div>
                                </div>
                                <div className="text-danger fw-bold" style={{ fontSize: "0.72rem" }}>
                                  ⚠️ {row.DiscrepancyReason}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Desktop Table View */}
                        <div className="d-none d-md-block table-responsive">
                          <table className="table table-hover table-sm mb-0 text-nowrap" style={{ fontSize: "0.78rem" }}>
                            <thead className="table-dark sticky-top">
                              <tr>
                                <th>Cont No</th>
                                <th>Date</th>
                                <th>Item</th>
                                <th>Seller Party</th>
                                <th>Buyer Party</th>
                                <th className="text-end">Qty (MT)</th>
                                <th>Farq Ka Kaaran (Discrepancy Reason)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredSelection.map((row, idx) => (
                                <tr key={idx}>
                                  <td className="fw-bold">{row.ContractNo}</td>
                                  <td>{row.ContractDate}</td>
                                  <td>{row.ItemName}</td>
                                  <td>
                                    <span className={row.IsSellerSelected ? "fw-bold text-success" : "text-muted"}>
                                      {row.SellerName}
                                      {row.IsSellerSelected && " (Selected)"}
                                    </span>
                                  </td>
                                  <td>
                                    <span className={row.IsBuyerSelected ? "fw-bold text-success" : "text-muted"}>
                                      {row.BuyerName}
                                      {row.IsBuyerSelected && " (Selected)"}
                                    </span>
                                  </td>
                                  <td className="text-end fw-bold">{parseFloat(row.Qty || 0).toFixed(2)}</td>
                                  <td>
                                    <Badge bg="warning" className="text-dark">
                                      {row.DiscrepancyReason}
                                    </Badge>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default QtyDifferenceModal;
